import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { AnimatePresence, motion } from "framer-motion";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import { LinearAiBeam } from "@/components/ui/linear-ai-beam";

const ACTION_BASE =
  "relative overflow-clip shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] disabled:cursor-not-allowed";

export const LINEAR_CLASSNAME =
  "before:bg-linear-to-t before:h-full before:w-full before:absolute before:top-0 before:left-0 hover:before:opacity-50 overflow-hidden relative before:transition-all before:z-0 before:duration-300 before:pointer-events-none";

export const LINEAR_AI_CONTENT_CLASSNAME =
  "relative z-10 flex h-full w-full min-w-0 items-center justify-center gap-[inherit] rounded-[inherit]";

export const LINEAR_AI_CONTENT_STYLE = {
  background:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.05) 30%, rgba(255, 255, 255, 0.01) 100%)",
} satisfies React.CSSProperties;

export const LINEAR_AI_BORDER_CLASSNAME =
  "pointer-events-none absolute inset-0 z-0 rounded-[inherit] border border-white/10";

const LINEAR_AI_CONTENT_SIZE_CLASSNAME: Record<string, string> = {
  default: "px-4 py-2 has-[>svg]:px-3",
  text: "",
  "text-xs": "",
  xs: "px-1.5",
  xxs: "px-1.5",
  sm: "px-2.5 has-[>svg]:px-2",
  lg: "px-6 has-[>svg]:px-4",
  icon: "p-0",
  "icon-xs": "p-0",
  "icon-sm": "p-0",
  "icon-lg": "p-0",
  auth: "px-3.5 py-2.5",
  tab: "px-3 py-2",
};

export function getLinearAiContentSizeClassName(size: string | null | undefined) {
  return LINEAR_AI_CONTENT_SIZE_CLASSNAME[size ?? "default"];
}

const buttonVariants = cva(
  "inline-flex shrink-0 duration-200 cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        outline:
          "border bg-surface-primary shadow-xs hover:bg-surface-hover dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        ghost: "hover:opacity-80",
        "ghost-destructive": "text-contrast/40 hover:text-destructive",
        link: "text-primary underline-offset-4 hover:underline",
        primary: `${ACTION_BASE} bg-primary border border-contrast/[0.15] text-primary-foreground hover:bg-primary/90 active:bg-primary/95`,
        destructive: `${ACTION_BASE} bg-destructive border border-contrast/[0.15] text-background dark:text-destructive-foreground hover:bg-desstructive/95`,
        "destructive-subtle": `${ACTION_BASE} bg-[#ED6664]/10 border border-red-300 dark:border-[#FFDDDD]/10 text-red-700 dark:text-[#FFDDDD] hover:bg-[#FFDDDD]/15 active:bg-[#FFDDDD]/20`,
        success: `${ACTION_BASE} bg-success/10 border border-success/20 text-success hover:bg-success/15 active:bg-success/20`,
        "success-subtle": `${ACTION_BASE} bg-[#66DC7E]/10 border border-green-300 dark:border-[#DDFFE4]/10 text-green-700 dark:text-[#DDFFE4] hover:bg-[#DDFFE4]/15 active:bg-[#DDFFE4]/20`,
        "info-subtle": `${ACTION_BASE} bg-[#4C9AFF]/10 border border-blue-300 dark:border-[#CFE4FF]/10 text-blue-700 dark:text-[#CFE4FF] hover:bg-[#CFE4FF]/15 active:bg-[#CFE4FF]/20`,
        secondary: `${ACTION_BASE} bg-contrast/4 border border-contrast/[0.15] text-heading hover:bg-contrast/8 active:bg-contrast/10`,
        light: `${ACTION_BASE} bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-white border border-contrast/[0.15] dark:text-[#171717] dark:hover:bg-white/90 active:scale-[0.98]`,
        "ai-solid": `${ACTION_BASE} bg-ai border border-contrast/[0.15] text-brand-foreground hover:bg-ai/90 active:bg-ai/95`,
        muted: `${ACTION_BASE} bg-contrast/4 border border-contrast/[0.15] text-heading opacity-30`,
        "muted-destructive":
          "bg-muted text-muted-foreground hover:bg-muted hover:text-destructive active:bg-muted/80",
        "muted-ghost": `text-muted-foreground hover:text-muted-foreground/50`,
        linear: cn(
          "before:from-(--gradient-color)/40 before:to-(--gradient-color)/5",
          "border-composition",
          LINEAR_CLASSNAME,
        ),
        "linear-light": cn(
          "before:from-(--foreground)/20 before:dark:from-(--foreground)/50 dark:before:from-(--foreground)/20 before:to-(--foreground)/5 text-foreground",
          "border-composition",
          LINEAR_CLASSNAME,
        ),
        "linear-contact": cn(
          "before:from-(--contact)/20 before:dark:from-(--contact)/50 dark:before:from-(--contact)/20 before:to-(--contact)/5 text-contact",
          "border-composition",
          LINEAR_CLASSNAME,
        ),
        "linear-aqua": cn(
          "before:from-blue-500/15 before:to-blue-500/5 text-blue-700 dark:text-blue-300",
          "border-composition",
          LINEAR_CLASSNAME,
        ),
        "linear-ai": cn(
          "relative isolate overflow-hidden bg-[#181410] text-[#fff1df] shadow-[0px_1px_2px_0px_rgb(0_0_0/0.16)]",
          "active:scale-[0.98]",
        ),
        ai: cn(
          "relative isolate overflow-hidden rounded-full border border-black/15 text-[#171717]",
          "bg-[linear-gradient(90deg,#ffdb8e_32%,#ffdfb2_42%,#ffaf53_65%)]",
          "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] active:scale-[0.98]",
          "[&_[data-slot=linear-ai-beam-track]]:bg-[radial-gradient(circle,#fffaec_0%,#fff5d8_34%,rgb(255_245_216/0.4)_56%,transparent_72%)]",
        ),
        tab: cn(
          "rounded-none bg-transparent font-medium text-contrast/40 transition-colors hover:text-contrast/60",
          "data-[active=true]:text-heading",
          "relative after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:bg-heading after:opacity-0 data-[active=true]:after:opacity-100",
          "[&>[data-slot=button-inset]]:hidden",
        ),
        /** Search results feedback (Hanna) — thumbs up, dark surfaces */
        "feedback-positive": cn(
          ACTION_BASE,
          "h-7 gap-1 px-2 py-1 text-sm font-medium leading-5 shadow-none",
          "[&>[data-slot=button-inset]]:hidden",
          "border border-[rgb(255_206_207/0.15)] bg-[rgb(255_175_83/0.04)] text-[#ffebd4] hover:bg-[rgb(255_175_83/0.08)]",
          "[&_svg]:text-[#ffebd4]",
          "data-[active=true]:border-[rgb(206_255_216/0.15)] data-[active=true]:bg-ai data-[active=true]:text-[#171717] data-[active=true]:hover:bg-ai",
          "data-[active=true]:[&_svg]:text-[#171717]",
        ),
        /** Same control on light / high-contrast strips (e.g. printed-style banners) */
        "feedback-positive-light": cn(
          ACTION_BASE,
          "h-7 gap-1 px-2 py-1 text-sm font-medium leading-5 shadow-none",
          "[&>[data-slot=button-inset]]:hidden",
          "border border-ai/25 bg-white/90 text-foreground/90 hover:bg-ai/10",
          "[&_svg]:text-ai",
          "data-[active=true]:border-ai/45 data-[active=true]:bg-ai data-[active=true]:text-brand-foreground data-[active=true]:hover:bg-ai",
          "data-[active=true]:[&_svg]:text-brand-foreground",
        ),
        /** Search results feedback — thumbs down, dark surfaces */
        "feedback-negative": cn(
          ACTION_BASE,
          "h-7 gap-1 px-2 py-1 text-sm font-medium leading-5 shadow-none",
          "[&>[data-slot=button-inset]]:hidden",
          "border border-[rgb(255_206_207/0.15)] bg-[rgb(255_206_207/0.04)] text-[#ffcecf] hover:bg-[rgb(255_206_207/0.08)]",
          "[&_svg]:text-[#ffcecf]",
          "data-[active=true]:border-[rgb(255_206_207/0.3)] data-[active=true]:bg-[rgb(255_206_207/0.12)] data-[active=true]:hover:bg-[rgb(255_206_207/0.12)]",
        ),
        "feedback-negative-light": cn(
          ACTION_BASE,
          "h-7 gap-1 px-2 py-1 text-sm font-medium leading-5 shadow-none",
          "[&>[data-slot=button-inset]]:hidden",
          "border border-red-200/90 bg-white/90 text-red-800/90 hover:bg-red-50",
          "[&_svg]:text-red-600",
          "data-[active=true]:border-red-300 data-[active=true]:bg-red-100 data-[active=true]:text-red-900 data-[active=true]:hover:bg-red-100",
          "data-[active=true]:[&_svg]:text-red-700",
        ),
      },
      size: {
        default:
          "h-9 px-4 py-2 has-[>svg]:px-3 [&_svg:not([class*='size-'])]:size-4",
        text: "font-normal text-sm",
        "text-xs": "font-normal text-xs",
        xs: "h-5.5 px-1.5 text-xs leading-none gap-0.5 rounded-sm [&_svg:not([class*='size-'])]:size-3",
        xxs: "h-5 !text-[.4rem] gap-1 px-1.5 [&_svg:not([class*='size-'])]:!size-3 relative before:content-[''] before:absolute before:min-h-10 before:left-0 before:right-0 before:top-1/2 before:-translate-y-1/2",
        sm: "h-7 gap-1.5 rounded-sm px-2.5 has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-4",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-9 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 [&_svg:not([class*='size-'])]:size-4",
        "icon-lg": "size-10 [&_svg:not([class*='size-'])]:size-4",
        auth: "w-full py-2.5 px-3.5 rounded-md font-normal [&_svg:not([class*='size-'])]:size-4",
        tab: "h-auto gap-1.5 px-3 py-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  asChild?: boolean;
  icon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, icon, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot.Root : "button";
    const isLinearAi = variant === "linear-ai";
    const isAi = variant === "ai";

    if (asChild) {
      return (
        <Comp
          type="button"
          data-slot="button"
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        type="button"
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          isLinearAi && "!p-0",
        )}
        ref={ref}
        {...props}
      >
        {isLinearAi ? (
          <>
            <span
              data-slot="linear-ai-border"
              className={LINEAR_AI_BORDER_CLASSNAME}
            />
            <LinearAiBeam />
            <span
              data-slot="linear-ai-content"
              className={cn(
                LINEAR_AI_CONTENT_CLASSNAME,
                getLinearAiContentSizeClassName(size),
              )}
              style={LINEAR_AI_CONTENT_STYLE}
            >
              {icon}
              {children}
            </span>
          </>
        ) : isAi ? (
          <>
            <LinearAiBeam />
            <span className="relative z-[2] inline-flex items-center gap-[inherit]">
              {icon}
              {children}
            </span>
          </>
        ) : (
          <>
            {icon}
            {children}
            <span
              data-slot="button-inset"
              className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
            />
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface SubmitButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  asChild?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  icon?: React.ReactNode;
}

const SubmitButton = React.forwardRef<HTMLButtonElement, SubmitButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingLabel,
      icon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot.Root : "button";
    const isLinearAi = variant === "linear-ai";
    const isAi = variant === "ai";

    if (asChild) {
      return (
        <Comp
          type="submit"
          data-slot="button"
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    const resolvedLoadingLabel =
      loadingLabel ?? (typeof children === "string" ? children : "Loading");

    const spinner = (
      <motion.span
        key="spinner"
        className="absolute inline-flex"
        initial={{ opacity: 0, scale: 0, rotate: -180 }}
        animate={{ opacity: 0.7, scale: 1, rotate: 0 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <Icon name="IconLoadingCircle" size={16} className="animate-spin" />
      </motion.span>
    );

    const content = (
      <>
        {loading ? resolvedLoadingLabel : children}
        {(icon || loading) && (
          <span className="relative inline-flex items-center justify-center size-4">
            <AnimatePresence mode="wait" initial={false}>
              {loading ? (
                spinner
              ) : icon ? (
                <motion.span
                  key="icon"
                  className="absolute inline-flex"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
                >
                  {icon}
                </motion.span>
              ) : null}
            </AnimatePresence>
          </span>
        )}
      </>
    );

    return (
      <Comp
        ref={ref}
        type="submit"
        data-slot="button"
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(
          buttonVariants({ variant, size, className }),
          isLinearAi && "!p-0",
        )}
        {...props}
      >
        {isLinearAi ? (
          <>
            <span
              data-slot="linear-ai-border"
              className={LINEAR_AI_BORDER_CLASSNAME}
            />
            <LinearAiBeam />
            <span
              data-slot="linear-ai-content"
              className={cn(
                LINEAR_AI_CONTENT_CLASSNAME,
                getLinearAiContentSizeClassName(size),
              )}
              style={LINEAR_AI_CONTENT_STYLE}
            >
              {content}
            </span>
          </>
        ) : isAi ? (
          <>
            <LinearAiBeam />
            <span className="relative z-[2] inline-flex items-center gap-[inherit]">
              {content}
            </span>
          </>
        ) : (
          <>
            {content}
            <span
              data-slot="button-inset"
              className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
            />
          </>
        )}
      </Comp>
    );
  },
);
SubmitButton.displayName = "SubmitButton";

export { Button, buttonVariants, SubmitButton };
